import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { useNavigate } from "react-router-dom";
import { Eye, FileText } from "lucide-react";
import { Button } from "@shared/ui/button";

import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useBulkPaymentBatches } from "../../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

const BulkPaymentList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE;

  const columns: TableColumn<any>[] = [
    {
      key: "batchId",
      header: "Batch Id",
      type: "text",
    },
    {
      key: "file",
      header: "File",
      type: "custom",
      render: (value: any) => (
        <div className="py-2">
          <div className="text-gray-900">{value.name}</div>
          <div className="text-gray-600">{value.size}</div>
        </div>
      ),
    },
    {
      key: "format",
      header: "Format",
      type: "text",
    },
    {
      key: "uploaded",
      header: "Uploaded",
      type: "custom",
      render: (value: any) => (
        <div className="py-2">
          <div className="text-gray-900">{value.time}</div>
          <div className="text-gray-600">{value.user}</div>
        </div>
      ),
    },
    {
      key: "total",
      header: "Total",
      type: "text",
    },
    {
      key: "matched",
      header: "Matched",
      type: "custom",
      render: (value: number) => (
        <span className="text-green-600 font-semibold text-sm">{value}</span>
      ),
    },
    {
      key: "exceptions",
      header: "Exceptions",
      type: "custom",
      render: (value: number) => (
        <span className="text-red-600 font-semibold text-sm">{value}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value: string) => (
        <span className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap inline-flex items-center">
          {value}
        </span>
      ),
    },
  ];

  const action: TableAction<any>[] = [
    {
      label: "View Details",
      onClick: (row: any) =>
        navigate(`/cx/payments/bulk-payment/details/${row.id}`),
      icon: <Eye className="h-4 w-4" />,
    },
  ];

  const { data: batchesData, isLoading } = useBulkPaymentBatches({
    remoteUtilityId: Number(remoteUtilityId),
    page: currentPage,
    pageSize: itemsPerPage,
  });

  const batches = batchesData?.results || [];
  const totalItems = batchesData?.count || 0;

  const formattedData = batches.map((batch: any) => {
    return {
      id: batch.id,
      batchId: batch.batchId,
      file: {
        name: batch.fileName,
        size: batch.fileSize ? `${(batch.fileSize / 1024).toFixed(0)} KB` : "",
      },
      format: batch.fileFormat,
      uploaded: { time: batch.createdAt, user: batch.uploadedBy },
      total: batch.totalRecords,
      matched: batch.matchedCount,
      exceptions: batch.exceptionsCount,
      status: batch.statusDisplay,
    };
  });

  return (
    <GenericTable
      data={formattedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search batches..."
      searchParamName="query"
      actions={action}
      actionsType="dropdown"
      onSearch={() => {}}
      pagination={{
        currentPage: currentPage,
        totalPages: Math.ceil(totalItems / itemsPerPage),
        totalItems: totalItems,
        itemsPerPage,
        startItem: (currentPage - 1) * itemsPerPage + 1,
        endItem: Math.min(currentPage * itemsPerPage, totalItems),
      }}
      onPageChange={setCurrentPage}
      emptyMessage="No bulk payments found"
      emptyIcon={<FileText className="h-8 w-8 text-muted-foreground mb-2" />}
    />
  );
};

export default BulkPaymentList;
